import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, CheckCircle } from 'lucide-react';
import StandardNavbar from '../components/StandardNavbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(true); // Valor inicial

  // ✅ NUEVO: Sincronizar isLogin con los parámetros de URL
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'register') {
      setIsLogin(false);
      setRegistrationStep(1); // Resetear al paso 1 si viene de URL
    } else {
      setIsLogin(true);
    }
  }, [searchParams]);


  // ✅ AGREGAR ESTO - Scroll al top al cargar
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  // Estados para registro con verificación
  const [registrationStep, setRegistrationStep] = useState(1); // 1: datos, 2: código
  
  const [formData, setFormData] = useState({
    name: '',
    apellido: '',
    email: '',
    telefono: '',
    fecha_nacimiento: '',
    password: '',
    confirmPassword: '',
    verificationCode: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // LOGIN (sin cambios)
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Inicio de sesión exitoso' });
        setTimeout(() => {
          if (result.user.role === 'admin') {
            navigate('/admin');
          } else {
            navigate('/');
          }
        }, 1500);
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Error de conexión. Intenta nuevamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  // REGISTRO - PASO 1: Solicitar código
  const handleRequestVerification = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    // Validaciones
    if (!formData.name.trim() || !formData.apellido.trim()) {
      setMessage({ type: 'error', text: 'El nombre y apellido son obligatorios' });
      setIsLoading(false);
      return;
    }
    if (!formData.telefono.trim()) {
      setMessage({ type: 'error', text: 'El teléfono es obligatorio' });
      setIsLoading(false);
      return;
    }
    if (!formData.fecha_nacimiento) {
      setMessage({ type: 'error', text: 'La fecha de nacimiento es obligatoria' });
      setIsLoading(false);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Las contraseñas no coinciden' });
      setIsLoading(false);
      return;
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(formData.password)) {
      setMessage({ type: 'error', text: 'La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula y un número' });
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/request-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          apellido: formData.apellido,
          email: formData.email,
          telefono: formData.telefono,
          fecha_nacimiento: formData.fecha_nacimiento,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Código enviado a tu email' });
        setRegistrationStep(2);
      } else {
        setMessage({ type: 'error', text: data.message || 'Error al enviar código' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Error de conexión. Intenta nuevamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  // REGISTRO - PASO 2: Verificar código y crear usuario
  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    if (formData.verificationCode.length !== 6) {
      setMessage({ type: 'error', text: 'Ingresa el código de 6 dígitos' });
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-and-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          code: formData.verificationCode
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Guardar token y usuario
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        setMessage({ type: 'success', text: '¡Registro exitoso! Redirigiendo...' });
        
        setTimeout(() => {
          navigate('/');
          window.location.reload(); // Para actualizar el contexto de Auth
        }, 1500);
      } else {
        setMessage({ type: 'error', text: data.message || 'Código inválido' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Error de conexión. Intenta nuevamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setRegistrationStep(1);
    setFormData({
      name: '',
      apellido: '',
      email: '',
      telefono: '',
      fecha_nacimiento: '',
      password: '',
      confirmPassword: '',
      verificationCode: ''
    });
    setMessage({ type: '', text: '' });
  };

  return (
    <>
      <Helmet>
        <title>{isLogin ? 'Iniciar Sesión' : 'Registrarse'} | Noche y Día Turismo</title>
        <meta name="description" content={isLogin ? 'Inicia sesión en tu cuenta' : 'Crea tu cuenta nueva'} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <StandardNavbar />
      
      <div className="relative pt-16 min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-12 flex items-center justify-center">
          <div className="max-w-md w-full">
            {/* Logo y título */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-[#1E3A5F] mb-2 font-montserrat">
                {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
              </h1>
              <p className="text-gray-600">
                {isLogin 
                  ? 'Accede a tu cuenta para gestionar tus viajes' 
                  : (registrationStep === 1 
                      ? 'Únete a nuestra comunidad de viajeros' 
                      : 'Verifica tu email para completar el registro'
                    )
                }
              </p>
            </div>

            {/* Formulario */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              {/* Indicador de pasos para registro */}
              {!isLogin && (
                <div className="flex justify-between mb-8">
                  {[1, 2].map((num) => (
                    <div key={num} className="flex items-center flex-1">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          registrationStep >= num
                            ? 'bg-[#2AA8B0] text-white'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                      >
                        {registrationStep > num ? <CheckCircle size={20} /> : num}
                      </div>
                      {num < 2 && (
                        <div
                          className={`flex-1 h-1 mx-2 ${
                            registrationStep > num ? 'bg-[#2AA8B0]' : 'bg-gray-200'
                          }`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {message.text && (
                <div className={`mb-6 p-4 rounded-lg ${
                  message.type === 'success' 
                    ? 'bg-green-100 border border-green-400 text-green-700' 
                    : 'bg-red-100 border border-red-400 text-red-700'
                }`}>
                  {message.text}
                </div>
              )}

              {/* LOGIN */}
              {isLogin && (
                <form onSubmit={handleLogin} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-colors"
                      placeholder="Ej: amartinez@mailhost.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contraseña *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-colors"
                      placeholder="Mínimo 6 caracteres"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#2AA8B0] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#1E3A5F] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Procesando...' : 'Iniciar Sesión'}
                  </button>
                </form>
              )}

              {/* REGISTRO - PASO 1: Datos */}
              {!isLogin && registrationStep === 1 && (
                <form onSubmit={handleRequestVerification} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-colors"
                        placeholder="Ej: Andrea"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Apellido *
                      </label>
                      <input
                        type="text"
                        name="apellido"
                        value={formData.apellido}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-colors"
                        placeholder="Ej: Martinez"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-colors"
                      placeholder="Ej: 11 4432 8675"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Nacimiento *
                    </label>
                    <input
                      type="date"
                      name="fecha_nacimiento"
                      value={formData.fecha_nacimiento}
                      onChange={handleInputChange}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-colors"
                      placeholder="Ej: amartinez@mailhost.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contraseña *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-colors"
                      placeholder="Mínimo 6 caracteres"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmar Contraseña *
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-colors"
                      placeholder="Repite tu contraseña"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#2AA8B0] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#1E3A5F] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Enviando código...' : 'Continuar'}
                  </button>
                </form>
              )}

              {/* REGISTRO - PASO 2: Código */}
              {!isLogin && registrationStep === 2 && (
                <form onSubmit={handleVerifyAndRegister} className="space-y-6">
                  <div className="text-center mb-6">
                    <Mail className="w-16 h-16 text-[#2AA8B0] mx-auto mb-4" />
                    <p className="text-gray-600">
                      Enviamos un código de verificación a<br />
                      <strong>{formData.email}</strong>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Código de 6 dígitos
                    </label>
                    <input
                      type="text"
                      name="verificationCode"
                      value={formData.verificationCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setFormData(prev => ({ ...prev, verificationCode: value }));
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2AA8B0] text-center text-2xl font-bold tracking-widest"
                      placeholder="000000"
                      maxLength={6}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Revisa tu bandeja de entrada y spam
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || formData.verificationCode.length !== 6}
                    className="w-full bg-[#2AA8B0] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#1E3A5F] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Verificando...' : 'Completar Registro'}
                  </button>

                  <button
                    type="button"
                    onClick={() => setRegistrationStep(1)}
                    className="w-full text-gray-600 hover:text-gray-800 text-sm"
                  >
                    Cambiar datos o reenviar código
                  </button>
                </form>
              )}

              {/* Toggle entre login y registro */}
              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  {isLogin ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}
                  <button
                    type="button"
                    onClick={toggleMode}
                    className="ml-2 text-[#2AA8B0] hover:text-[#1E3A5F] font-medium transition-colors"
                  >
                    {isLogin ? 'Regístrate aquí' : 'Inicia sesión aquí'}
                  </button>
                </p>
              </div>

              {isLogin && (
                <div className="text-center mt-4">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#2AA8B0] hover:text-[#1E8A91] font-medium"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              )}

              {/* ✅ NUEVO: Aviso para usuarios del sitio anterior */}
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <span className="text-amber-500 text-xl">⚠️</span>
                  <div>
                    <p className="text-sm text-amber-800 font-medium mb-1">
                      Importante para usuarios anteriores
                    </p>
                    <p className="text-xs text-amber-700">
                      Si tenías una cuenta creada antes del 01/12/2025, deberás volver a registrarte. 
                      Por motivos de seguridad, todas las cuentas anteriores fueron eliminadas.
                    </p>
                  </div>
                </div>
              </div>

              {isLogin && (
                <div className="text-center mt-4">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#2AA8B0] hover:text-[#1E8A91] font-medium"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              )}
            </div>

            {/* Beneficios de crear cuenta */}
            <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-bold text-[#1E3A5F] mb-4">
                {isLogin ? 'Beneficios de tener cuenta' : '¿Por qué crear una cuenta?'}
              </h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-[#2AA8B0] mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Guarda tus viajes favoritos
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-[#2AA8B0] mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Acceso a ofertas exclusivas
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-[#2AA8B0] mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Historial de reservas
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-[#2AA8B0] mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Notificaciones de nuevos destinos
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
}

export default Login;